import type { ReactNode, MouseEventHandler, ChangeEventHandler, FormEvent, RefObject } from 'react';

import { forwardRef, useCallback, useEffect, useState } from 'react';

import useUniqueId from '@core/hooks/useUniqueId';
import Button from '@ui/Button';
import Flex from '@ui/Flex';
import FormGroup from '@ui/FormGroup';
import Icon from '@ui/Icon';
import Input from '@ui/Input';
import Menu from '@ui/Menu';
import MenuDivider from '@ui/Menu/Divider';
import MenuItem from '@ui/Menu/Item';
import Toggle from '@ui/Toggle';

import classes from './style.module.scss';

export interface LinkEditorProps {
  /**
   * A base url for all links
   */
  baseUrl?: string;
  /**
   * An optional footer
   */
  footer?: ReactNode;
  /**
   * Operation for removing the current link decorator
   */
  handleRemoveLink?: MouseEventHandler<HTMLButtonElement>;
  /**
   * Initial value for the label input
   */
  label?: string;
  /**
   * Callback fired on a change event
   */
  onChange?: ChangeEventHandler<HTMLInputElement>;
  /**
   * Callback fired on a submit event
   */
  onSubmit?: (e: FormEvent, data: { label: string; url: string }) => void;
  /**
   * Indicates whether all input fields are required or not. If required, input
   * validity constraints must be met before the form can be submit. Error
   * messages are shown in the UI to help users know what to do.
   */
  required?: boolean;
  /**
   * Initial value for the open in new tab target toggle
   */
  target?: string;
  /**
   * Initial value for the url input
   */
  url?: string;
  /**
   * Ref for the url input element
   */
  urlInputRef?: RefObject<HTMLInputElement>;
  /**
   * Enable menu items for open in new tab and remove link
   */
  useExtraAttrs?: boolean;
  useMDX?: boolean;
}

/*
 * This was originally from an old version of 'api-explorer' package. It was
 * copied over to the `@readme/markdown` package. It is now no longer used in
 * either place. It has mostly been superceded by the `useExpandCustomProtocol`
 * hook. However, in this LinkEditor component, I think we still want to show
 * the fully exanded URL, maybe?
 *
 * @todo: refactor `useExpandCustomProtocol` to return a fully expanded URL
 */
export function getHref(href: string, baseUrl: string) {
  const [path, hash] = href.split('#');
  const hashStr = hash ? `#${hash}` : '';

  const base = baseUrl === '/' ? '' : baseUrl;
  const doc = path.match(/^doc:([-_a-zA-Z0-9#]*)$/);

  if (doc) {
    return `${base}/docs/${doc[1]}${hashStr}`;
  }

  const ref = path.match(/^ref:([-_a-zA-Z0-9#]*)$/);
  if (ref) {
    return `${base}/reference-link/${ref[1]}${hashStr}`;
  }

  // we need to perform two matches for changelogs in case
  // of legacy links that use 'blog' instead of 'changelog'
  const blog = path.match(/^blog:([-_a-zA-Z0-9#]*)$/);
  const changelog = path.match(/^changelog:([-_a-zA-Z0-9#]*)$/);
  const changelogMatch = blog || changelog;
  if (changelogMatch) {
    return `${base}/changelog/${changelogMatch[1]}${hashStr}`;
  }

  const custompage = path.match(/^page:([-_a-zA-Z0-9#]*)$/);
  if (custompage) {
    return `${base}/page/${custompage[1]}${hashStr}`;
  }

  return href;
}

/**
 * A menu to add/edit a name and a URL for a link.
 * Note that our editor relies on this component.
 */
const LinkEditor = forwardRef<HTMLElement, LinkEditorProps>(
  (
    {
      baseUrl = '',
      onSubmit,
      onChange,
      url,
      urlInputRef,
      label,
      footer,
      required,
      handleRemoveLink,
      target,
      useExtraAttrs,
      useMDX,
    }: LinkEditorProps,
    ref,
  ) => {
    const uid = useUniqueId('LinkEditor');
    const [formError, setFormError] = useState<Record<'label' | 'url', string | null>>({
      label: null,
      url: null,
    });

    const [currentUrl, setCurrentUrl] = useState(url || '');
    useEffect(() => {
      setCurrentUrl(url || '');
    }, [url]);

    const handleUrlChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
      e => {
        setCurrentUrl(e.target.value);
        onChange?.(e);
      },
      [onChange],
    );

    /**
     * Check validity of our form inputs and returns `true` if every input
     * satisfies their validation constraints.
     */
    const reportValidity = useCallback((labelValue: string, urlValue: string) => {
      setFormError({
        label: labelValue ? null : 'Label is required',
        url: urlValue ? null : 'URL is required',
      });
      return labelValue && urlValue;
    }, []);

    const handleSubmit = useCallback(
      e => {
        e.preventDefault();
        e.stopPropagation();
        const formElement = e.currentTarget;
        const labelValue = formElement.querySelector('input[name=label]')?.value || '';
        const urlValue = formElement.querySelector('input[name=url]')?.value || '';

        if (required && !reportValidity(labelValue, urlValue)) return;

        onSubmit?.(e, { label: labelValue, url: urlValue });
      },
      [onSubmit, reportValidity, required],
    );

    return (
      <Flex ref={ref} align="stretch" layout="col" onSubmit={handleSubmit} tag="form">
        <Menu className={classes.LinkEditor}>
          <MenuItem focusable={false} TagName="div">
            <FormGroup errorMessage={formError.label} htmlFor={uid('label')} label="Label">
              <Input
                id={uid('label')}
                name="label"
                onChange={onChange}
                placeholder="label"
                size="sm"
                value={label || ''}
              />
            </FormGroup>
          </MenuItem>
          <MenuDivider />
          <MenuItem focusable={false} TagName="div">
            <FormGroup
              className={classes['LinkEditor-URL']}
              errorMessage={formError.url}
              htmlFor={uid('url')}
              label="URL"
            >
              <Input
                ref={urlInputRef}
                id={uid('url')}
                name="url"
                onChange={handleUrlChange}
                placeholder="url"
                size="sm"
                value={currentUrl}
              />
              <Button
                href={currentUrl ? getHref(currentUrl, baseUrl.replace(/\/$/, '')) : ''}
                link
                size="xs"
                target="_blank"
                text
              >
                <Icon name="arrow-up-right" />
              </Button>
            </FormGroup>
          </MenuItem>
          {!!useExtraAttrs && !!useMDX && (
            <>
              <MenuDivider />
              <MenuItem focusable={false} TagName="div">
                <FormGroup htmlFor={uid('target')}>
                  <Toggle
                    checked={target === '_blank'}
                    label="Open in new tab"
                    name="target"
                    onChange={onChange}
                    type="toggle"
                  />
                </FormGroup>
              </MenuItem>
              <MenuDivider />
              <MenuItem color="red" icon={<Icon name="trash" />} onClick={handleRemoveLink} TagName="button">
                Remove link
              </MenuItem>
            </>
          )}
          {footer || <button style={{ display: 'none' }} />}
        </Menu>
      </Flex>
    );
  },
);

LinkEditor.displayName = 'LinkEditor';

export default LinkEditor;
